<script setup>
import { computed } from 'vue'
import Layout from '@pages/Shared/layouts/AdminLayout.vue'
import Card from '@pages/Shared/Card.vue'
import { PhBuilding, PhStudent, PhBank, PhUserCircle } from '@phosphor-icons/vue'

import { Bar, Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

/* ================= PROPS ================= */
const props = defineProps({
  statistics: Object,
  sections: Array
})

/* ================= SUMMARY COUNTS ================= */
const staffCount = computed(() => props.statistics.staff)
const studentCount = computed(() => props.statistics.students)
const sectionCount = computed(() => props.statistics.sections)
const classCount = computed(() => props.statistics.classes)

/* ================= REGISTER CHART ================= */
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
)

/* ================= STUDENTS BY SECTION ================= */
const sectionChart = computed(() => {
  const labels = props.sections.map(s => s.name)
  const data = props.sections.map(
    s => s.classes?.reduce(
      (sum, cls) => sum + Number(cls.students_count ?? 0),
      0
    ) || 0
  )

  return {
    labels,
    datasets: [
      {
        label: 'Students',
        data,
        backgroundColor: [
          'rgba(34,197,94,0.85)',
          'rgba(59,130,246,0.85)',
          'rgba(168,85,247,0.85)',
          'rgba(234,179,8,0.85)'
        ],
        borderRadius: 12,
        barThickness: 42
      }
    ]
  }
})

/* ================= STUDENTS PER CLASS ================= */
const classChart = computed(() => {
  const classes = props.sections.flatMap(s => s.classes || [])
  const labels = classes.map(c => c.name)
  const data = classes.map(c => Number(c.students_count ?? 0))

  return {
    labels,
    datasets: [
      {
        label: 'Students',
        data,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.18)',
        fill: true,
        tension: 0.45,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#22c55e'
      }
    ]
  }
})

/* ================= BASE CHART OPTIONS ================= */
const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#111827',
      padding: 12,
      titleFont: { size: 13 },
      bodyFont: { size: 13 }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: { color: '#e5e7eb' },
      ticks: { stepSize: 10, color: '#6b7280' }
    }
  }
}

const sectionChartOptions = {
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    x: {
      grid: { display: false },
      ticks: { color: '#6b7280' }
    }
  }
}

const classChartOptions = {
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    x: {
      grid: { display: false },
      ticks: {
        color: '#6b7280',
        maxRotation: 45,
        minRotation: 45,
        autoSkip: false,
        padding: 10
      }
    }
  }
}
</script>

<template>
  <Layout>
    <div class="flex flex-row justify-between items-start md:items-center mb-6 gap-4">
      <h2 class="text-2xl font-semibold text-primary">Dashboard</h2>
    </div>

    <div class="grid gap-6 mb-12 md:grid-cols-2 xl:grid-cols-4">
      <Card title="Staff" :subtitle="staffCount" color="bg-orange-100">
        <PhUserCircle :size="32" class="text-orange-500" />
      </Card>

      <Card title="Students" :subtitle="studentCount" color="bg-green-100">
        <PhStudent :size="32" class="text-green-600" />
      </Card>

      <Card title="Sections" :subtitle="sectionCount" color="bg-primary-100">
        <PhBuilding :size="32" class="text-primary-500" />
      </Card>

      <Card title="Classes" :subtitle="classCount" color="bg-purple-100">
        <PhBank :size="32" class="text-purple-500" />
      </Card>
    </div>

    <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
      <!-- Section Chart -->
      <div class="bg-white rounded-md shadow-md border border-gray-100 p-6">
        <div class="mb-4">
          <h3 class="text-lg font-semibold text-gray-700">
            Student Distribution by Section
          </h3>
          <p class="text-sm text-gray-500">
            Enrollment across academic programs
          </p>
        </div>

        <div class="h-[220px] sm:h-[260px] md:h-[280px]">
          <Bar
            v-if="sectionChart.labels.length"
            :data="sectionChart"
            :options="sectionChartOptions"
          />
          <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">
            No data available
          </div>
        </div>
      </div>

      <!-- Class Chart -->
      <div class="bg-white rounded-md shadow-md border border-gray-100 p-6 md:col-span-2">
        <div class="mb-4">
          <h3 class="text-lg font-semibold text-gray-700">
            Class Enrollment Overview
          </h3>
          <p class="text-sm text-gray-500">
            Student population per class
          </p>
        </div>

        <div class="overflow-x-auto">
          <div class="h-[220px] sm:h-[260px] md:h-[280px] min-w-full md:min-w-[1000px]">
            <Line
              v-if="classChart.labels.length"
              :data="classChart"
              :options="classChartOptions"
            />
            <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">
              No data available
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>